import {Box} from 'src/design/atoms/Box';
import React, {useState} from 'react';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {useAlert} from 'src/shared/hooks/useAlert';

import {useInViewport} from '@mantine/hooks';
import type {NotificationModuleEnum} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useGetNotificationListQuery} from 'src/redux/queries/notification';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {NotificationsList} from './NotificationsList';

const maxPageRows = 10;
export interface NotificationsModuleProps {
  entityId?: number;
  notificationRuleId?: number;
  notificationType?: NotificationModuleEnum;
  recipientId?: number;
  includeDescendents?: boolean;
}

export const NotificationsModule: React.FC<NotificationsModuleProps> = ({
  entityId,
  notificationRuleId,
  notificationType,
  recipientId,
  includeDescendents,
}) => {
  const notifRulesPerms = useModulePermissions(ModuleEnum.NotificationRules);

  const {setAlert} = useAlert();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const {ref, inViewport} = useInViewport();

  const {data, isFetching, isError, error} = useGetNotificationListQuery(
    {
      start: (currentPage - 1) * maxPageRows,
      length: maxPageRows,
      module: notificationType,
      notificationRuleId,
      entityId,
      recipientId,
      includeDescendents,
    },
    {skip: !inViewport || !notifRulesPerms.view}
  );

  React.useEffect(() => {
    if (isError) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [isError, error]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => setCurrentPage(value);

  return (
    <>
      <DataHeader
        titleText="Notifications"
        isFilterActive={false}
        titleSize="md"
      />
      <Box ref={ref} style={{marginBottom: 240}}>
        <NotificationsList
          isLoading={isFetching}
          data={data?.data ?? []}
          currentPage={currentPage}
          recordsPerPage={maxPageRows}
          totalRecords={data?.totalRecords ?? 0}
          onPageChange={handlePageChange}
        />
      </Box>
    </>
  );
};
